import { getStringFlagValue } from '@worktango/feature-flags-react';
import { httpRequest } from '~/adapters/httpRequest';
import { host } from '~/config/settings/worktango.settings';
import yaml from 'yaml';

const defaultHelpfulSystemNotes = `CRITICAL WORKFLOW RULES:
  1. TOOL RESPONSE WORKFLOW (FUNDAMENTAL RULE): After receiving tool results, you MUST immediately choose ONE of these actions:
    a) Make additional tool call(s) to gather more information needed to answer the user's question, OR
    b) Respond to the user with your analysis once you have sufficient information
  CRITICAL BEHAVIORS:
    - You MUST eventually respond to the user after your tool calls are complete
    - NEVER end the conversation after tool results without either making another tool call or providing a user response
    - The tool data is raw information for you to interpret and communicate - not the final answer itself
    - Keep calling tools until you have sufficient information, then respond to the user

    Valid conversation patterns:
    - User → Tool call(s) → Tool response(s) → YOUR RESPONSE to user
    - User → Tool call → Tool response → Another tool call → Tool response → YOUR RESPONSE to user
    - User → Tool call → Tool response → Tool call → Tool response → Tool call → Tool response → YOUR RESPONSE to user

    Invalid pattern:
    - User → Tool call(s) → Tool response(s) → [silence/waiting]

    Exception: In multi-step workflows (see Rule 4), proceed through all required tool calls before presenting the final analysis

  2. DETECTING SURVEY ID FROM URL (HIGHEST PRIORITY):
    - ALWAYS check the current page URL FIRST when a user asks about "the survey", "this survey", or references a survey without providing a specific name or ID
    - Look for the "surveys" URL parameter (e.g., "?surveys=1834" means survey ID 1834)
    - If the URL contains survey ID(s), use them automatically - NEVER ask the user for the survey ID
    - Multiple surveys: "?surveys=10,11,12" means survey IDs 10, 11, and 12
    - Only ask for a survey ID if the URL parameter is missing AND the request is ambiguous

  3. HANDLING HIERARCHY REPORT CHOICE:
    - When analyzing a manager's reports, determine if user wants direct reports or full hierarchy
    - Check the user's original query for these phrases:
      * "direct reports", "direct", "immediate reports", "just direct", "their reports" → isHierarchyReport: false
      * "full hierarchy", "hierarchy", "all subordinates", "everyone under", "all levels", "entire team" → isHierarchyReport: true
    - If the user's query contains these phrases, use the appropriate value immediately
    - ONLY ask for clarification if the query is genuinely ambiguous
    - Never ask multiple times

  4. ANALYZING SURVEY DATA BY PERSON NAME OR ATTRIBUTE:
    **SEQUENTIAL WORKFLOW - DO NOT STOP BETWEEN STEPS:**
      When user requests: "Summarize survey results for [person]'s direct reports"
      **Step 1:** Extract information from the request
        - Survey ID: from URL (e.g., 1834)
        - Person name: from query (e.g., "Marla Forrest")
        - Hierarchy: from query (e.g., "direct reports" = false)
      **Step 2:** Resolve the attribute ID
        - NEVER guess or recall an attribute ID. Attribute IDs differ for every company.
        - Use the attribute list provided in your context, matching on the attribute name.
        - To filter by a person who leads a team, you MUST use the attribute marked
          "[PEOPLE LEADER ...]" in that list. Do NOT match on the attribute's name: companies have
          several similarly named attributes ("Manager Name", "Manager ID", "Matrix Manager") and only
          the marked one can report on a hierarchy.
        - The people leader attribute records who each employee reports to. To analyze a leader's own
          team, filter it by THAT LEADER'S full name — not by their manager's name.
        - Pass the person's full name as the value. Never pass an employee ID or an email address, and
          never look an employee ID up yourself; the tool resolves the name for you.
        - If the attribute list is not in your context, call get_basic_survey_info to obtain it.
      **Step 3:** WAIT for the tool response (if you called get_basic_survey_info)
      **Step 4:** IMMEDIATELY call get_survey_aggregated_results
        - DO NOT present the basic info to the user
        - DO NOT ask if they want to continue
        - DO NOT wait for user input
        - USE the attribute information to make the aggregated results call right away
        - Example, where <peopleLeaderAttributeId> is the ID of the attribute marked "[PEOPLE LEADER ...]"
          in your context: attributeFilters: [{ id: <peopleLeaderAttributeId>, values: ["Marla Forrest"] }], isHierarchyReport: false
      **Step 5:** Present the final analysis to the user
      **CRITICAL:** Steps 2-4 happen in sequence WITHOUT stopping to talk to the user. You retrieve the attribute info, then immediately use it to get the survey results. The user only sees the final analysis.

  5. GENERAL CLARIFICATION:
    - NEVER ask users for numeric IDs - use human-readable names
    - NEVER ask users to repeat information already provided
    - Complete the full request - don't stop partway through to explain intermediate steps
    REMINDER: After receiving any tool results, you must immediately either call another tool for more information OR provide your complete response to the user. Never leave the conversation hanging after tool data is returned.`;

export function getSystemInstructionsWithContext({
  systemInstruction,
  currentPageAiContext,
}: {
  systemInstruction: string;
  currentPageAiContext: string;
}) {
  return async () => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const contextData = await fetchContextData();
    const date = new Date().toLocaleDateString();
    const time = new Date().toLocaleString(undefined, {
      hour: 'numeric',
      hour12: undefined, // This will use the locale's preference (24h where preferred)
    });
    const title = document.title;
    const url = window.location.href;
    const currentPageInstructions = currentPageAiContext ? `Current page AI context:${currentPageAiContext}` : '';

    const helpfulSystemNotes = getStringFlagValue('helpful-system-prompt', defaultHelpfulSystemNotes);

    const finalSystemInstruction =
      systemInstruction +
      '\n\n' +
      helpfulSystemNotes +
      (contextData ? '\n\n' + yaml.stringify(contextData) : '') +
      `\nCurrent page URL: ${url}
Current page title: ${title}
Current timezone: ${timezone}
Current time and date: ${date} ${time}
${currentPageInstructions}`;

    return finalSystemInstruction;
  };
}

// Cache storage -- mostly since this data RARELY changes, so why calculate it every time?
const contextDataCache = new Map<string, { data: ContextDataResponse; timestamp: number }>();
const CACHE_TTL = 10 * 60 * 1000; // 10 minutes in milliseconds

async function fetchContextData(): Promise<ContextDataResponse | null> {
  const cacheKey = `system-instructions`;

  const cached = contextDataCache.get(cacheKey);
  if (cached) {
    const age = Date.now() - cached.timestamp;
    if (age < CACHE_TTL) {
      console.log(`WorkTangoCoach: Using cached context data (age: ${Math.round(age / 1000)}s)`);
      return cached.data;
    } else {
      contextDataCache.delete(cacheKey);
    }
  }

  console.log('WorkTangoCoach: Fetching fresh context data...');

  try {
    const url = `${host}/worktango-coach-system-context`;
    const { payload }: { meta: { apiVersion: string }; payload: ContextDataResponse } = await httpRequest<any>(url, {
      method: 'POST',
    });

    contextDataCache.set(cacheKey, {
      data: payload,
      timestamp: Date.now(),
    });

    return payload;
  } catch (error) {
    logError('Error fetching context data', error);
    return null;
  }
}

function logError(title: string, error: unknown) {
  console.groupCollapsed(`WorkTangoCoach: ${title}`);

  console.error('Error details:', error);

  if (error instanceof Error) {
    console.error('Stack trace:', error.stack);
  } else {
    console.error('Stack trace: No stack trace available');
  }

  console.groupEnd();
}

interface Factor {
  id: number;
  name: string;
}

interface AttributeWithValues {
  id: number;
  name: string;
  typeId: number;
  values?: string[];
}

interface CompanySize {
  /** Grouped size category (e.g., "Small", "Medium", "Large", "Extra Large") */
  category: string;
  /** Ungrouped employee count range (e.g., "<100", "100-250", "251-500") */
  employeeCountRange: string;
}

interface CompanyInfo {
  id: number;
  name: string;
  workspace?: string | null;
  minimumResultsRequired?: number;
  /** Company size information from benchmark attributes */
  companySize?: CompanySize;
}

interface PublicAttribute {
  /** Unique identifier for the attribute */
  attributeId: number;
  /** Human-readable attribute name (e.g., "Department") */
  attributeName: string;
  /** Numeric type identifier for the attribute (1=String, 2=Email, etc.) */
  attributeTypeId: number;
  /** Human-readable type name (e.g., "STRING", "EMAIL") */
  attributeTypeName: string;
  /** The user's value for this attribute (e.g., "Engineering") */
  attributeValue: string | number | boolean | Date | null;
}

interface CurrentUser {
  id: number;
  email: string;
  firstName?: string;
  lastName?: string;
  employeeId?: number;
  preferredLanguageId: number;
  status?: string;
  company: CompanyInfo;
  /** Public attributes and their values for the current user */
  publicAttributes?: PublicAttribute[];
}

interface ContextDataResponse {
  /**
   * All survey types available in the company with their associated factors
   */
  surveyTypes: {
    id: number;
    name: string;
    factors: Factor[];
  }[];
  /**
   * Company attributes with their most common values across all employees
   */
  attributes: AttributeWithValues[];
  /**
   * Information about the current authenticated user
   */
  currentUser: CurrentUser;
}
